import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Link } from 'react-router-dom';
import { useMobileMenu } from './Footer';

const Features: React.FC = () => {
  const { language } = useLanguage();
  const { closeMobileMenu } = useMobileMenu();

  const featuresData = {
    en: [
      {
        id: 'customerManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        title: 'Customer Management',
        description: 'Complete customer management features that help you store all customer-related data in one place. Track contacts, customer history and preferences.'
      },
      {
        id: 'documentManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        title: 'Document Management',
        description: 'Storage and management of customer-specific documents. Easily create, store and track contracts, quotes and other documents.'
      },
      {
        id: 'worksheetManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 7h6m-6 4h4m8 0h-4m4 0l-4 4m0-8l4-4m-4 0h4" />
          </svg>
        ),
        title: 'Worksheet Management',
        description: 'Efficient worksheet management system for transparent record keeping of services, work phases and tasks. Helps in accurately tracking work time, material costs and services.'
      },
      {
        id: 'quoteManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        ),
        title: 'Quotation Creation',
        description: 'Create and send professional quotes with a single click. Track quote status and feedback.'
      },
      {
        id: 'contractManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        ),
        title: 'Contract Management',
        description: 'Create, store and manage contracts with ease. Automatic notifications when contracts expire or need renewal.'
      },
      {
        id: 'templateCreation',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
        ),
        title: 'Template Creation',
        description: 'Create customizable templates for quotes, contracts and other documents. Saves time and ensures consistency.'
      },
      {
        id: 'salesAutomation',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        ),
        title: 'Sales Processes',
        description: 'Automate sales processes, track opportunities, and increase closing rates. Guide salespeople step by step.'
      },
      {
        id: 'reporting',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        title: 'Custom Reports',
        description: 'Custom reports and dashboards based on KPIs and metrics important to your business. Real-time data and performance indicators.'
      },
      {
        id: 'mobileFriendly',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        ),
        title: 'Mobile-Friendly Interface',
        description: 'All features are accessible from mobile devices. Work as efficiently on your phone or tablet as you would on a desktop computer.'
      },
      {
        id: 'dataMigration',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        ),
        title: 'Data Migration',
        description: 'Quickly and easily switch to YouCRM with our data migration service from your existing system. Our experienced professionals minimize transition time and ensure data integrity.'
      },
      {
        id: 'ticketingSystem',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
          </svg>
        ),
        title: 'Ticketing System',
        description: 'Efficient support and issue tracking module that helps resolve problems quickly and increase customer satisfaction. The system can be expanded as needed.'
      },
    ],
    hu: [
      {
        id: 'customerManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        ),
        title: 'Ügyfélkezelés',
        description: 'Teljes ügyfélkezelési funkciók, amelyek segítenek minden ügyféllel kapcsolatos adatot egy helyen tárolni. Nyomon követheti a kapcsolatfelvételeket, ügyféltörténetet és preferenciákat.'
      },
      {
        id: 'documentManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        ),
        title: 'Dokumentumkezelés',
        description: 'Ügyfélspecifikus dokumentumok tárolása és kezelése. Könnyen létrehozhat, tárolhat és nyomon követhet szerződéseket, árajánlatokat és egyéb dokumentumokat.'
      },
      {
        id: 'worksheetManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 7h6m-6 4h4m8 0h-4m4 0l-4 4m0-8l4-4m-4 0h4" />
          </svg>
        ),
        title: 'Munkalap kezelés',
        description: 'Hatékony munkalap kezelő rendszer a szolgáltatások, munkafázisok és feladatok átlátható nyilvántartására. Segít a munkaidő, anyagköltségek és szolgáltatások pontos követésében.'
      },
      {
        id: 'quoteManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
          </svg>
        ),
        title: 'Árajánlat készítés',
        description: 'Professzionális árajánlatok készítése és küldése egyetlen kattintással. Az árajánlatok állapotának és a visszajelzéseknek nyomon követése.'
      },
      {
        id: 'contractManagement',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        ),
        title: 'Szerződéskezelés',
        description: 'Szerződések készítése, tárolása és kezelése egyszerűen. Automatikus értesítések a szerződések lejáratakor vagy megújításakor.'
      },
      {
        id: 'templateCreation',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
          </svg>
        ),
        title: 'Sablonok létrehozása',
        description: 'Testreszabható sablonok létrehozása árajánlatokhoz, szerződésekhez és más dokumentumokhoz. Időt takarít meg és biztosítja a konzisztenciát.'
      },
      {
        id: 'salesAutomation',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
          </svg>
        ),
        title: 'Értékesítési folyamatok',
        description: 'Automatizálja az értékesítési folyamatokat, kövesse nyomon a lehetőségeket, és növelje az üzletkötési arányokat. Lépésről lépésre vezeti az értékesítőket.'
      },
      {
        id: 'reporting',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        ),
        title: 'Testreszabott jelentések',
        description: 'Egyedi jelentések és műszerfalak az Ön vállalkozásának fontos KPI-k és mérőszámok alapján. Valós idejű adatok és teljesítménymutatók.'
      },
      {
        id: 'mobileFriendly',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
          </svg>
        ),
        title: 'Mobilbarát felület',
        description: 'Minden funkció elérhető mobil eszközökről is. Telefonon vagy tableten is ugyanolyan hatékonyan dolgozhat, mint asztali számítógépen.'
      },
      {
        id: 'dataMigration',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        ),
        title: 'Adatmigráció',
        description: 'Meglévő rendszeréből való adatmigráció segítségével gyorsan és egyszerűen válthat a YouCRM-re. Tapasztalt szakembereink minimalizálják az átállási időt és biztosítják az adatok integritását.'
      },
      {
        id: 'ticketingSystem',
        icon: (
          <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
          </svg>
        ),
        title: 'Jegykezelő rendszer',
        description: 'Hatékony támogatás- és hibabejelentés-kezelési modul, amely segít a problémák gyors megoldásában és az ügyfelek elégedettségének növelésében. Igény szerint bővíthető a rendszer.'
      },
    ]
  };

  // Select features based on current language
  const features = language === 'en' ? featuresData.en : featuresData.hu;

  // Localized header and section texts
  const headerTexts = {
    en: {
      tagline: 'YouCRM',
      title: 'Complete CRM Functionality',
      subtitle: 'Every feature can be customized to your unique business needs',
      ctaButton: 'Request a Personalized Demo'
    },
    hu: {
      tagline: 'YouCRM',
      title: 'Teljes körű CRM funkcionalitás',
      subtitle: 'Minden funkció személyre szabható az Ön egyedi üzleti igényeinek megfelelően',
      ctaButton: 'Kérjen személyre szabott bemutatót'
    }
  };

  const localizedTexts = language === 'en' ? headerTexts.en : headerTexts.hu;

  return (
    <div className="bg-gray-50 py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <h2 className="text-base font-semibold text-indigo-600 tracking-wide uppercase">{localizedTexts.tagline}</h2>
          <p className="mt-1 text-4xl font-extrabold text-gray-900 sm:text-5xl sm:tracking-tight lg:text-6xl">
            {localizedTexts.title}
          </p>
          <p className="mt-3 max-w-2xl mx-auto text-xl text-gray-500 sm:mt-4">
            {localizedTexts.subtitle}
          </p>
        </div>

        <div className="mt-16">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {features.map((feature) => (
              <div key={feature.id} className="bg-white overflow-hidden shadow rounded-lg">
                <div className="p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0 bg-gradient-to-r from-indigo-700 to-purple-500 rounded-md p-3 text-white">
                      {feature.icon}
                    </div>
                    <h3 className="ml-3 text-lg font-medium text-gray-900">
                      {feature.title}
                    </h3>
                  </div>
                  <div className="mt-4">
                    <p className="text-base text-gray-500">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <Link 
              to="/contact" 
              onClick={closeMobileMenu}
              className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700"
            >
              {localizedTexts.ctaButton}
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Features; 